import useBannerData from "hook/useBannerData";
import { Link } from "react-router-dom";
import { useEffect, useCallback, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";

const Banner = () => {
  const { banners, firstBanner, loadingBanner } = useBannerData();

  const autoplayRef = useRef(null);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false });

  const [enableSlider, setEnableSlider] = useState(false);

  // 🟢 Only enable slider when user sees banner (reduces JS work)
  useEffect(() => {
    const timer = setTimeout(() => {
      setEnableSlider(true);
    }, 1500); // reduced from 5s → 1.5s

    return () => clearTimeout(timer);
  }, []);

  // 🟢 Autoplay ONLY when slider is active
  useEffect(() => {
    if (!emblaApi || !enableSlider) return;

    autoplayRef.current = setInterval(() => {
      if (emblaApi.canScrollNext()) {
        emblaApi.scrollNext();
      } else {
        emblaApi.scrollTo(0);
      }
    }, 5000);

    return () => clearInterval(autoplayRef.current);
  }, [emblaApi, enableSlider]);

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  if (loadingBanner) {
    return (
      <div className="relative md:max-w-8xl mx-auto px-4 pt-4 md:pb-4 pb-0">
        <div className="w-full h-[150px] sm:h-[220px] md:h-[300px] lg:h-[400px] rounded-md overflow-hidden relative bg-gray-300">
          {/* Shimmer gradient animation */}
          <div
            className="absolute inset-0 bg-gradient-to-r from-gray-300 via-gray-100 to-gray-300 
                        animate-shimmer bg-[length:200%_100%]"
          />
        </div>
      </div>
    );
  }

  if (!banners || banners.length === 0) return null;

  return (
    <div className="relative md:max-w-8xl mx-auto px-4 pt-4 md:pb-4 pb-0">
      {/* 🟢 First banner always static (fast LCP) */}
      {!enableSlider && firstBanner && (
        <div className="w-full h-[150px] sm:h-[220px] md:h-[300px] lg:h-[400px]">
          <img
            src={`https://backend.nuhababy.com/${firstBanner.image_path}`}
            alt={firstBanner.title}
            className="w-full h-full object-cover rounded-md"
            loading="eager"
          />
        </div>
      )}

      {/* 🔵 Slider loads later (reduces initial JS execution) */}
      {enableSlider && banners.length > 1 && (
        <div className="relative overflow-hidden rounded-md" ref={emblaRef}>
          <div className="flex">
            {banners.slice(1).map((slide) => (
              <div
                key={slide.banner_id}
                className="min-w-full h-[150px] sm:h-[220px] md:h-[300px] lg:h-[400px]"
              >
                <Link to={slide.link}>
                  <img
                    src={`https://backend.nuhababy.com/${slide.image_path}`}
                    alt={slide.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </Link>
              </div>
            ))}
          </div>

          {/* NAV */}
          <button
            onClick={scrollPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 text-white w-10 h-10 rounded-full"
          >
            ‹
          </button>

          <button
            onClick={scrollNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 text-white w-10 h-10 rounded-full"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
};

export default Banner;
